/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { useParams } from "react-router-dom"
import { AdminCompanyShowWrapper } from "./AdminCompaniesShowPageWrapper"

import { IRecord } from "../../types/schema"
import { useSkeem } from "../../providers/SkeemProvider"
import React, { useState } from "react"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { useApi } from "../../providers/ApiProvider"
import { H2 } from "@cd2/text/build/es6"
import { Spacer, Button, Grid, IconButton } from "@cd2/components/build/es6"
import { Card } from "../../interfaceParts/Card"
import { LoadingFrame } from "../user/UserResultsShowPage/AssessmentDetailsTab"
import { useAsyncCallback } from "../../hooks/useAsyncCallback"
import { icons } from "../../utils/icons"
import { useHelpRequestDialog } from "../../interfaceParts/HelpButton"
import { ButtonGrid } from "../employees/EmployeesTasksPage"
import { formatPennies, pluralize } from "../../utils/string"
import { Pill } from "../../interfaceParts/Pill"
import { siteColors } from "../../providers/ThemeProvider"
import { assert, doo } from "@cd2/tools"
import { Flex } from "../../interfaceParts/Flex"
import { ToggleSwitchField } from "../../interfaceParts/ToggleSwitch"
import { useQuickConfirmationModal2 } from "../../hooks/useQuickConfirmationModal"
import { LabelInfo } from "../../interfaceParts/LabelInfo"
import { useDialogueConfirmation2 } from "../../hooks/useDialogConfirmation"
import { useMutatorState } from "../../hooks/useMutatorState"
import { Fields } from "@cd2/forms"
import { SaveButton } from "../user/UserManageEmployeePage/UserManageEmployeeLoginCredentialsTab"
import { CancelButton } from "../../interfaceParts/CancelButton"
import { FieldGroup } from "../../interfaceParts/FieldGroup"
import { TabsList } from "../../interfaceParts/TabbedSection"
import { useLocationReload } from "../../hooks/locationReloadKey"
import { useSiteAlerts } from "../../hooks/useSiteAlerts"
import { messageFromError } from "../../utils/error"
import { renderTable } from "../user/renderTable"
import { KeyValueList } from "../../interfaceParts/KeyValueList"
import { NewSimpleSelectField } from "../../interfaceParts/NewSimpleSelectField"
import { useGlobalState } from "../../utils/globalState"
import { billingDesyncNote } from "../../utils/billingDesync"

export function AdminCompanyBillingPage() {
  const globalState = useGlobalState()
  const companyId = useParams<{ id: string }>().id
  const canViewAdminBillingSection = globalState.user.isAdmin

  return (
    <AdminCompanyShowWrapper companyId={companyId} active="billing">
      <CompanyEditSubscriptionSection companyId={companyId} />
      {canViewAdminBillingSection && (
        <React.Fragment>
          <Spacer size={64} />
          <SuperAdminStripeDetails companyId={companyId} />
        </React.Fragment>
      )}
    </AdminCompanyShowWrapper>
  )
}

type ICompany = IRecord<
  "companies",
  | "planName"
  | "pricePennies"
  | "chargeFrequency"
  | "freeAccount"
  | "email"
  | "addressLine1"
  | "addressLine2"
  | "addressCity"
  | "addressCounty"
  | "addressPostcode"
  | "isHeadCompany"
  | "archived"
> & {
  userCount: number
  plan: IRecord<
    "companyPlans",
    | "name"
    | "minUsers"
    | "maxUsers"
    | "pricePenniesPerUser"
    | "includesOneHourHR"
    | "type"
    | "oneHourHRPricePennies"
    | "totalPricePennies"
    | "chargeFrequency"
  > | null
}

export function CompanyEditSubscriptionSection({ companyId }: { companyId: string }) {
  const skeem = useSkeem()
  const [company, setCompany] = useState<ICompany | null>(null)

  const [reloadKey, triggerReload] = useLocationReload()

  useAsyncEffect(async () => {
    setCompany(null)

    const company = await skeem.fetch2<ICompany | null>({
      first: {
        name: "companies",
        attributes: [
          "planName",
          "pricePennies",
          "chargeFrequency",
          "freeAccount",
          "email",
          "addressLine1",
          "addressLine2",
          "addressCity",
          "addressCounty",
          "addressPostcode",
          "isHeadCompany",
          "archived",
          {
            as: "userCount",
            value: {
              count: {
                attr: {
                  name: "users",
                  filter: {
                    matches: { archived: false, isAnonymous: false, isLeaver: false },
                  },
                },
              },
            },
          },
          {
            name: "plan",
            attributes: [
              "name",
              "minUsers",
              "maxUsers",
              "pricePenniesPerUser",
              "includesOneHourHR",
              "type",
              "oneHourHRPricePennies",
              "totalPricePennies",
              "chargeFrequency",
            ],
          },
        ],
        matches: { id: companyId },
      },
    })
    assert(company, "Company not found")

    // For head companies, include users from sub-companies in the billing count (sub-companies have billing disabled)
    if (company.isHeadCompany) {
      const totalUserCount = await skeem.fetch2<number>({
        count: {
          name: "skeem_users",
          matches: {
            archived: false,
            isAnonymous: false,
            isLeaver: false,
            company: { or: [companyId, { matches: { headCompany: companyId } }] },
          },
        },
      })
      company.userCount = totalUserCount
    }

    setCompany(company)
  }, [companyId, reloadKey])

  const handleViewDashboard = useAsyncCallback(async () => {
    const { url } = await window.api.getStripeDashboardLink({
      companyId,
      returnTo: window.location.href,
    })
    window.open(url, "_blank")
  })

  const triggerEditEmail = useEditEmailDialog()
  const handleEditEmail = useAsyncCallback(async () => {
    if (!company) return

    const shouldReload = await triggerEditEmail({
      companyId,
      email: company.email,
    })
    if (shouldReload) triggerReload()
  })

  const triggerEditBillingAddress = useEditBillingAddressDialog()
  const handleEditBillingAddress = useAsyncCallback(async () => {
    if (!company) return

    const shouldReload = await triggerEditBillingAddress({
      companyId,
      address: {
        line1: company.addressLine1,
        line2: company.addressLine2,
        city: company.addressCity,
        county: company.addressCounty,
        postcode: company.addressPostcode,
      },
    })
    if (shouldReload) triggerReload()
  })

  const triggerHelpRequestDialog = useHelpRequestDialog()
  const handleHelpRequest = useAsyncCallback(async () => {
    await triggerHelpRequestDialog()
  })

  if (!company) {
    return <LoadingFrame />
  }

  return (
    <div>
      <H2>Billing</H2>

      {company.archived && (
        <Card border>
          <span
            css={css`
              font-size: 18px;
              font-weight: bold;
            `}
          >
            Not billed
          </span>
          <br />
          <div>This company is archived.</div>
        </Card>
      )}
      {!company.archived && !company.freeAccount && (
        <Card border>
          {company.plan && (
            <div>
              {company.plan.type !== "custom" && (
                <div>
                  <div
                    css={css`
                      font-size: 18px;
                      font-weight: bold;
                    `}
                  >
                    {doo(() => {
                      let total = company.plan!.pricePenniesPerUser * company.userCount
                      if (company.plan?.includesOneHourHR) {
                        total += company.plan.oneHourHRPricePennies
                      }
                      return formatPennies(total)
                    })}{" "}
                    per month + VAT
                  </div>
                  <Spacer size={8} />
                  <Flex alignItems="center" gap={8}>
                    {pluralize(company.userCount, `%c active user%s`)}
                    {" • "}
                    {formatPennies(company.plan?.pricePenniesPerUser)} per user
                  </Flex>
                  <Spacer size={4} />
                  {company.plan.includesOneHourHR && (
                    <Pill color={siteColors.pills.green}>
                      Includes 1 Hour HR Support at{" "}
                      {formatPennies(company.plan.oneHourHRPricePennies)}
                    </Pill>
                  )}
                  {!company.plan.includesOneHourHR && (
                    <Pill color={siteColors.pills.orange}>Does not include 1 Hour HR Support</Pill>
                  )}
                </div>
              )}
              {company.plan.type === "custom" && (
                <div>
                  <b>{company.plan.name}</b>
                  <Spacer size={4} />
                  <div
                    css={css`
                      font-size: 18px;
                      font-weight: bold;
                    `}
                  >
                    {formatPennies(company.plan.totalPricePennies!)} per{" "}
                    {company.plan.chargeFrequency.replace("ly", "")} + VAT
                  </div>
                  <Spacer size={8} />
                  {company.plan.includesOneHourHR && (
                    <Pill color={siteColors.pills.green}>Includes 1 Hour HR Support</Pill>
                  )}
                  {!company.plan.includesOneHourHR && (
                    <Pill color={siteColors.pills.orange}>Does not include 1 Hour HR Support</Pill>
                  )}
                </div>
              )}
              <Spacer size={8} />
              <Flex alignItems="end" gap={8}>
                <LabelInfo label="Billing Email" value={company.email} />
                <IconButton icon={icons.edit} onClick={handleEditEmail} buttonStyle="text" />
              </Flex>
              <Spacer size={8} />
              <Flex alignItems="end" gap={8}>
                <LabelInfo
                  label="Billing Address"
                  value={
                    <div>
                      {[
                        company.addressLine1,
                        company.addressLine2,
                        company.addressCity,
                        company.addressCounty,
                        company.addressPostcode,
                      ]
                        .filter(Boolean)
                        .map((line, i) => (
                          <div key={i}>{line}</div>
                        ))}
                    </div>
                  }
                />
                <IconButton
                  icon={icons.edit}
                  onClick={handleEditBillingAddress}
                  buttonStyle="text"
                />
              </Flex>
            </div>
          )}
          {!company.plan && <div>NO PLAN</div>}
        </Card>
      )}
      {!company.archived && company.freeAccount && (
        <Card border>
          <span
            css={css`
              font-size: 18px;
              font-weight: bold;
            `}
          >
            Free Account
          </span>
          <br />
          <div>You have unlimited access to the system</div>
        </Card>
      )}
      <Spacer />
      <ButtonGrid>
        <Button onClick={handleViewDashboard} rightIcon={icons.external} buttonStyle="outline">
          Manage my subscription
        </Button>
        <Button rightIcon="comments" onClick={handleHelpRequest} buttonStyle="outline">
          Ask for help
        </Button>
      </ButtonGrid>
    </div>
  )
}

function subscriptionDesyncMessage(
  company: { archived: boolean; freeAccount: boolean },
  subscription: object | null,
) {
  return billingDesyncNote(company, Boolean(subscription))
}

function SuperAdminStripeDetails({ companyId }: { companyId: string }) {
  const [reloadKey, triggerReload] = useLocationReload()
  const api = useApi()
  const triggerConfirmation = useQuickConfirmationModal2()
  const siteAlerts = useSiteAlerts()

  const [company, setCompany] = useState<IRecord<
    "companies",
    "billingNotes" | "flagBilling" | "enableBillingDashboard" | "archived" | "freeAccount"
  > | null>(null)
  const [subscription, setSubscription] = useState<
    Awaited<ReturnType<typeof api.getStripeSubscriptionDetails>> | "loading"
  >("loading")
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null)

  useAsyncEffect(async () => {
    const company = await window.skeem.fetch2<
      IRecord<
        "companies",
        "billingNotes" | "flagBilling" | "enableBillingDashboard" | "archived" | "freeAccount"
      >
    >({
      first: {
        name: "companies",
        attributes: [
          "billingNotes",
          "flagBilling",
          "enableBillingDashboard",
          "archived",
          "freeAccount",
        ],
        matches: { id: companyId },
      },
    })
    setCompany(company)
    try {
      const subscription = await api.getStripeSubscriptionDetails({ companyId })
      setSubscription(subscription)
    } catch (err) {
      console.error(err)
      setSubscriptionError(messageFromError(err))
    }
  }, [companyId, reloadKey])

  const handleSyncSubscription = useAsyncCallback(async () => {
    const shouldContinue = await triggerConfirmation(
      company?.archived
        ? {
            heading: "Cancel subscription",
            body: "This company is archived, so it should have no subscription. Syncing will cancel the Stripe subscription.",
            yesText: "Cancel subscription",
            requireCheckbox: true,
          }
        : {
            heading: "Sync Subscription",
            body: "This will update the subscription details in stripe. An invoice will automatically be sent if the amounts are different",
            yesText: "Sync Subscription",
            requireCheckbox: true,
          },
    )
    if (!shouldContinue) return
    await api.updateCompanySubscription(companyId)
    triggerReload()
  })

  const triggerEditPlan = useEditPlan()
  const handleEditPlan = useAsyncCallback(async () => {
    const shouldReload = await triggerEditPlan({ companyId })
    if (shouldReload) triggerReload()
  })

  const handleUpdateNotes = useAsyncCallback(async () => {
    if (!company) return
    await window.skeem.mutate({
      companies: {
        update: {
          id: companyId,
          billingNotes: company.billingNotes,
          flagBilling: company.flagBilling,
        },
      },
    })
    siteAlerts.genericSuccess("Saved")
  })

  const handleChangeEnableBillingDashboard = useAsyncCallback(async () => {
    if (!company) return

    const shouldContinue = await triggerConfirmation(
      company.enableBillingDashboard
        ? {
            heading: "Disable Billing Dashboard",
            body: "This company will lose access to the new billing dashboard and the old billing pages will be shown. Are you sure?",
            yesText: "Disable Billing Dashboard",
          }
        : {
            heading: "Enable Billing Dashboard",
            body: "This company will gain access to the new billing dashboard. Are you sure?",
            yesText: "Enable Billing Dashboard",
          },
    )
    if (!shouldContinue) return
    await window.skeem.mutate({
      companies: {
        update: {
          id: companyId,
          enableBillingDashboard: !company.enableBillingDashboard,
        },
      },
    })
    siteAlerts.genericSuccess("Saved")
    triggerReload()
  })

  const desyncMessage =
    subscription !== "loading" && company ? subscriptionDesyncMessage(company, subscription) : null

  return (
    <Card border>
      <h1>VISIBLE TO CG ADMIN ONLY</h1>
      <Spacer />
      {desyncMessage && (
        <React.Fragment>
          <div
            css={css`
              background: #f8d4d4;
              border: 1px solid #e7a3a3;
              border-radius: 4px;
              padding: 12px 16px;
              font-weight: 600;
            `}
          >
            {desyncMessage}
          </div>
          <Spacer />
        </React.Fragment>
      )}
      <Button onClick={handleEditPlan}>Edit Plan...</Button>
      <Spacer />
      {subscriptionError && (
        <div css={{ color: "red" }}>
          <b>Error loading subscription:</b> {subscriptionError}
        </div>
      )}
      {subscription !== "loading" && (
        <FieldGroup name="Stripe Data">
          <div>
            <b>Current Stripe Subscription:</b>
            {!subscription && <div>NO SUBSCRIPTION</div>}
            {subscription && (
              <div>
                {renderTable({
                  data: subscription.items,
                  columns: [
                    { heading: "Name", render: item => item.name },
                    { heading: "Quantity", render: item => item.quantity },
                    { heading: "Ex VAT", render: item => formatPennies(item.exVat) },
                    {
                      heading: "Inc VAT",
                      render: item => formatPennies(item.pricePennies),
                    },
                    {
                      heading: "Total",
                      render: item => formatPennies(item.pricePennies * item.quantity),
                    },
                  ],
                })}
                <div
                  css={css`
                    display: flex;
                    justify-content: end;
                    padding: 8px 32px;
                  `}
                >
                  <KeyValueList
                    labelWidth={100}
                    gap={4}
                    data={[
                      { label: "Ex VAT", value: formatPennies(subscription.exVat) },
                      { label: "Tax", value: `${subscription.taxRate}%` },
                      {
                        label: "Total (inc VAT)",
                        value: formatPennies(subscription.totalPennies),
                      },
                    ]}
                    inline
                  />
                </div>
              </div>
            )}
          </div>
          <details>
            <summary>Stripe Subscription Dump</summary>
            <pre>{JSON.stringify(subscription, null, 2)}</pre>
          </details>
        </FieldGroup>
      )}
      <Spacer />
      <Button onClick={handleSyncSubscription}>Sync subscription to stripe...</Button>
      <Spacer />
      {company && (
        <div
          css={css`
            padding: 16px;
            background: ${company.enableBillingDashboard ? "#d4f8d4" : "#f8d4d4"};
          `}
        >
          <H2>
            {company.enableBillingDashboard && "Billing dashboard enabled"}
            {!company.enableBillingDashboard && "Billing dashboard disabled"}
          </H2>
          <ToggleSwitchField
            label="Enable Billing Dashboard"
            value={company.enableBillingDashboard}
            onChange={handleChangeEnableBillingDashboard}
          />
        </div>
      )}
      <Spacer />
      {company && (
        <FieldGroup name="Admin Notes">
          <p>Make specific notes about their billing or flag any issues that need investigating</p>
          <Fields.TextArea
            label="Notes"
            value={company.billingNotes}
            onChange={value => setCompany({ ...company, billingNotes: value })}
            rows="auto"
          />
          <Spacer />
          <ToggleSwitchField
            label="Flag Billing"
            value={company.flagBilling}
            onChange={value => setCompany({ ...company, flagBilling: value })}
          />
          <Spacer />
          <SaveButton onClick={handleUpdateNotes} />
        </FieldGroup>
      )}
    </Card>
  )
}

function useEditPlan() {
  return useDialogueConfirmation2<boolean, { companyId: string }>({
    Component({ dialogData, onResolve, registerOutsideClick }) {
      registerOutsideClick(() => onResolve(false))

      const [state, mutate] = useMutatorState({
        loaded: false,
        type: null as null | "free" | "automaticPerUser" | "custom" | "customPerUser",
        planId: "" as string | null,
        planName: "",
        pricePenniesPerUser: null as number | null,
        includesOneHourHR: false,
        totalPricePennies: null as number | null,
        chargeFrequency: "monthly" as "monthly" | "yearly",
        oneHourHRPricePennies: null as number | null,

        errors: {} as {
          type?: string
          pricePenniesPerUser?: string
          totalPricePennies?: string
          oneHourHRPricePennies?: string
        },
      })

      useAsyncEffect(async () => {
        type Company = IRecord<"companies", "freeAccount"> & {
          plan: Plan | null
        }
        type Plan = IRecord<
          "companyPlans",
          | "name"
          | "type"
          | "pricePenniesPerUser"
          | "includesOneHourHR"
          | "oneHourHRPricePennies"
          | "totalPricePennies"
          | "chargeFrequency"
        >
        const company = await window.skeem.fetch2<Company>({
          first: {
            name: "companies",
            attributes: [
              "freeAccount",
              {
                name: "plan",
                attributes: [
                  "name",
                  "type",
                  "pricePenniesPerUser",
                  "includesOneHourHR",
                  "oneHourHRPricePennies",
                  "totalPricePennies",
                  "chargeFrequency",
                ],
              },
            ],
            matches: { id: dialogData.companyId },
          },
        })

        mutate.setPartial({
          loaded: true,
          type: company.freeAccount ? "free" : company.plan ? company.plan.type : null,
          planId: company.plan?.id ?? null,
          planName: company.plan?.name || "",
          pricePenniesPerUser: company.plan?.pricePenniesPerUser || null,
          totalPricePennies: company.plan?.totalPricePennies || null,
          oneHourHRPricePennies: company.plan?.oneHourHRPricePennies || null,
          includesOneHourHR: company.plan?.includesOneHourHR || false,
          chargeFrequency: company.plan?.chargeFrequency || "monthly",
        })
      }, [])

      const handleSubmit = useAsyncCallback(async () => {
        if (!state.loaded) return

        const planMutation = {
          name: state.planName,
          type: state.type,
          pricePenniesPerUser: 0,
          includesOneHourHR: false,
          oneHourHRPricePennies: 0,
          totalPricePennies: 0,
          minUsers: 0,
          maxUsers: 0,
          chargeFrequency: "monthly" as "monthly" | "yearly",
        }

        const errors = {} as (typeof state)["errors"]
        if (state.type === null) {
          errors.type = "is required"
        } else if (state.type === "custom") {
          if (!state.totalPricePennies || state.totalPricePennies <= 0) {
            errors.totalPricePennies = "is required"
          }
          planMutation.totalPricePennies = state.totalPricePennies!
          planMutation.chargeFrequency = state.chargeFrequency
        } else if (state.type === "automaticPerUser") {
          // These will be updated when syncing to stripe
          planMutation.name = "Basic Plan"
          planMutation.pricePenniesPerUser = 1500
          if (state.includesOneHourHR) {
            if (!state.oneHourHRPricePennies || state.oneHourHRPricePennies <= 0) {
              errors.oneHourHRPricePennies = "is required"
            }
            planMutation.includesOneHourHR = true
            planMutation.oneHourHRPricePennies = state.oneHourHRPricePennies!
          }
        } else if (state.type === "customPerUser") {
          if (!state.pricePenniesPerUser || state.pricePenniesPerUser <= 0) {
            errors.pricePenniesPerUser = "is required"
          }
          planMutation.pricePenniesPerUser = state.pricePenniesPerUser!
          if (state.includesOneHourHR) {
            if (!state.oneHourHRPricePennies || state.oneHourHRPricePennies <= 0) {
              errors.oneHourHRPricePennies = "is required"
            }
            planMutation.includesOneHourHR = true
            planMutation.oneHourHRPricePennies = state.oneHourHRPricePennies!
          }
        } else if (state.type === "free") {
          // do nothing
        } else {
          // @ts-expect-error
          throw new Error(`Unknown plan type ${state.type.toString()}`)
        }
        mutate.setProp("errors", errors)
        if (Object.keys(errors).length > 0) return

        if (state.type === "free") {
          await window.skeem.mutate({
            companies: {
              update: {
                id: dialogData.companyId,
                freeAccount: true,
                planName: "Free",
                usingNewBilling: true,
                plan: state.planId ? { destroy: state.planId } : undefined,
              },
            },
          })
        } else {
          await window.skeem.mutate({
            companies: {
              update: {
                id: dialogData.companyId,
                usingNewBilling: true,
                freeAccount: false,
                planName: planMutation.name,
                plan: state.planId
                  ? { update: { id: state.planId, ...planMutation } }
                  : { create: planMutation },
              },
            },
          })
        }

        onResolve(true)
      })

      if (!state.loaded) return <LoadingFrame />

      return (
        <Grid gap={12}>
          <H2>Edit Plan</H2>

          <div>
            <TabsList
              currentTab={state.type || ""}
              onTabChange={mutate.setter("type") as any}
              tabs={[
                { key: "free", title: "Free" },
                { key: "custom", title: "Custom" },
                { key: "automaticPerUser", title: "Automatic Per User" },
                { key: "customPerUser", title: "Custom Per User" },
              ]}
            />
            {state.errors.type && (
              <div
                css={css`
                  color: red;
                `}
              >
                {state.errors.type}
              </div>
            )}
          </div>
          {state.type === "free" && (
            <React.Fragment>
              <p>This company will be a free account.</p>
            </React.Fragment>
          )}
          {state.type === "custom" && (
            <React.Fragment>
              <Fields.Text
                label="Plan Name"
                value={state.planName}
                onChange={mutate.setter("planName")}
              />
              <Fields.Price
                label="Total Price (ex VAT)"
                value={state.totalPricePennies}
                onChange={mutate.setter("totalPricePennies")}
                inPennies
              />
              <NewSimpleSelectField
                label="Charge Frequency"
                value={state.chargeFrequency}
                onChange={opt => mutate.setProp("chargeFrequency", opt.value)}
                options={[
                  { value: "monthly", label: "Monthly" },
                  { value: "yearly", label: "Yearly" },
                ]}
                noSearch
              />
            </React.Fragment>
          )}
          {state.type === "customPerUser" && (
            <React.Fragment>
              <Fields.Text
                label="Plan Name"
                value={state.planName}
                onChange={mutate.setter("planName")}
              />
              <Fields.Price
                label="Price Per User (ex VAT)"
                value={state.pricePenniesPerUser}
                onChange={mutate.setter("pricePenniesPerUser")}
                inPennies
              />
              <ToggleSwitchField
                label="Includes 1 Hour HR Support"
                value={state.includesOneHourHR}
                onChange={mutate.setter("includesOneHourHR")}
              />
              {state.includesOneHourHR && (
                <Fields.Price
                  label="HR Support Price (ex VAT)"
                  value={state.oneHourHRPricePennies}
                  onChange={mutate.setter("oneHourHRPricePennies")}
                  inPennies
                />
              )}
            </React.Fragment>
          )}
          {state.type === "automaticPerUser" && (
            <React.Fragment>
              <div>
                Pricing will be determined based on the current plans.{" "}
                <a href="/billing/per-user-plans" target="_blank">
                  Manage Plans
                </a>
              </div>
              <Fields.Text
                label="Plan Name"
                value={state.planName}
                onChange={mutate.setter("planName")}
                disabled
              />
              <Fields.Price
                label="Price Per User (ex VAT)"
                value={state.pricePenniesPerUser}
                onChange={mutate.setter("pricePenniesPerUser")}
                disabled
                inPennies
              />
              <ToggleSwitchField
                label="Includes 1 Hour HR Support"
                value={state.includesOneHourHR}
                onChange={mutate.setter("includesOneHourHR")}
              />
              {state.includesOneHourHR && (
                <Fields.Price
                  label="HR Support Price (ex VAT)"
                  value={state.oneHourHRPricePennies}
                  onChange={mutate.setter("oneHourHRPricePennies")}
                  inPennies
                />
              )}
            </React.Fragment>
          )}
          <Spacer />
          <ButtonGrid>
            <SaveButton onClick={handleSubmit} />
            <CancelButton onClick={() => onResolve(false)} />
          </ButtonGrid>
        </Grid>
      )
    },
  })
}

function useEditEmailDialog() {
  return useDialogueConfirmation2<boolean, { companyId: string; email: string }>({
    Component({ dialogData, onResolve, registerOutsideClick }) {
      registerOutsideClick(() => onResolve(false))

      const api = useApi()

      const [email, setEmail] = useState(dialogData.email)

      const handleSubmit = useAsyncCallback(async () => {
        await window.skeem.mutate({
          companies: {
            update: {
              id: dialogData.companyId,
              email,
            },
          },
        })
        await api.syncStripeCompanyDetails({ companyId: dialogData.companyId })
        onResolve(true)
      })

      return (
        <Grid gap={12}>
          <H2>Edit Billing Email</H2>
          <Fields.Text label="Billing Email" value={email} onChange={setEmail} />
          <Spacer />
          <ButtonGrid>
            <SaveButton onClick={handleSubmit} />
            <CancelButton onClick={() => onResolve(false)} />
          </ButtonGrid>
        </Grid>
      )
    },
  })
}

function useEditBillingAddressDialog() {
  return useDialogueConfirmation2<
    boolean,
    {
      companyId: string
      address: {
        line1: string
        line2: string
        city: string
        county: string
        postcode: string
      }
    }
  >({
    Component({ dialogData, onResolve, registerOutsideClick }) {
      registerOutsideClick(() => onResolve(false))

      const api = useApi()

      const [state, mutate] = useMutatorState({
        ...dialogData.address,
        errors: {} as { line1?: string; postcode?: string },
      })

      const handleSubmit = useAsyncCallback(async () => {
        const errors = {} as (typeof state)["errors"]
        if (!state.line1) {
          errors.line1 = "is required"
        }
        if (!state.postcode) {
          errors.postcode = "is required"
        }
        mutate.setProp("errors", errors)
        if (Object.keys(errors).length > 0) return

        await window.skeem.mutate({
          companies: {
            update: {
              id: dialogData.companyId,
              addressLine1: state.line1,
              addressLine2: state.line2,
              addressCity: state.city,
              addressCounty: state.county,
              addressPostcode: state.postcode,
            },
          },
        })
        await api.syncStripeCompanyDetails({ companyId: dialogData.companyId })
        onResolve(true)
      })

      return (
        <Grid gap={12}>
          <H2>Edit Billing Address</H2>
          <Fields.Text
            label="Address Line 1"
            value={state.line1}
            onChange={mutate.setter("line1")}
          />
          <Fields.Text
            label="Address Line 2"
            value={state.line2}
            onChange={mutate.setter("line2")}
          />
          <Fields.Text label="City" value={state.city} onChange={mutate.setter("city")} />
          <Fields.Text label="County" value={state.county} onChange={mutate.setter("county")} />
          <Fields.Text
            label="Postcode"
            value={state.postcode}
            onChange={mutate.setter("postcode")}
          />
          <Spacer />
          <ButtonGrid>
            <SaveButton onClick={handleSubmit} />
            <CancelButton onClick={() => onResolve(false)} />
          </ButtonGrid>
        </Grid>
      )
    },
  })
}
